import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Printer, Download } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import TresoShell from "@/components/treso/TresoShell";
import { useFilePreview } from "@/context/FilePreviewContext";
import EchFilters from "@/components/treso/echeances/EchFilters";
import EchTable from "@/components/treso/echeances/EchTable";
import EchAnalysis from "@/components/treso/echeances/EchAnalysis";
import { EchCards, EchForecast, EchAging } from "@/components/treso/echeances/EchWidgets";
import { EMPTY_FILTERS, applyFilters, summarize, toServerFilters } from "@/components/treso/echeances/echShared";

const SQ = { width: 30, height: 30, borderRadius: 8, border: "1px solid #33333B", color: "#C9C9D1", display: "inline-flex", alignItems: "center", justifyContent: "center" };

/** iter425 lot B — Trésorerie › Échéances clients (refonte) : volet de filtres, cartes, prévision, ancienneté, tableau par client, relance. */
export default function TresoEcheances() {
  const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();
  const [data, setData] = useState({ items: [], projects: [], lot_types: [] });
  const [f, setF] = useState({ ...EMPTY_FILTERS });
  const [previewed, setPreviewed] = useState(false);
  const load = () => api.get("/treso/echeances/clients").then(({ data: d }) => setData(d)).catch(() => toast.error("Chargement des échéances impossible"));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => applyFilters(data.items, f), [data.items, f]);
  const rowsNoAge = useMemo(() => applyFilters(data.items, f, "age"), [data.items, f]);
  const clientsCount = new Set(data.items.map((r) => r.contact_id)).size;
  const subtitle = summarize(f, data.projects, clientsCount);
  const qs = `filters=${encodeURIComponent(JSON.stringify(toServerFilters(f)))}`;
  const pdfUrl = `/treso/reports/echeances_clients/export.pdf?${qs}`;
  return (
    <TresoShell subtitle="Échéances clients"
      fixed={
        <div className="flex items-center" style={{ gap: 12 }} data-testid="ech-header">
          <span className="inline-flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 9, background: "#1E2A3D" }}><CalendarClock size={18} style={{ color: "#3B9EFF" }} /></span>
          <div className="min-w-0" style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#E8E8EA" }}>Échéances clients</div>
            <div className="truncate" style={{ fontSize: 11, color: "#8A8A93" }} data-testid="ech-subtitle">{subtitle}</div>
          </div>
          <button type="button" onClick={() => { setPreviewed(true); openAuthenticatedUrl(pdfUrl, { filename: "Echeances_clients.pdf", contentType: "application/pdf" }); }} title="Aperçu du rapport (PDF) — Imprimer / Télécharger après aperçu" className="hover:bg-white/5" style={SQ} data-testid="ech-report-view"><Printer size={14} /></button>
          {previewed && <button type="button" onClick={buildDownloadHandler(pdfUrl, { filename: "Echeances_clients.pdf" })} title="Télécharger le rapport affiché (PDF)" className="hover:bg-white/5" style={SQ} data-testid="ech-report-download"><Download size={14} /></button>}
        </div>
      }>
      <div className="flex" style={{ gap: 14, alignItems: "flex-start" }} data-testid="ech-layout">
        <EchFilters rows={data.items} f={f} setF={setF} projects={data.projects} lotTypes={data.lot_types} />
        <div className="grid min-w-0" style={{ flex: 1, gap: 12 }}>
          <EchCards rows={rows} />
          <div className="grid" style={{ gridTemplateColumns: "minmax(0, 1fr) 300px", gap: 12 }}>
            <EchForecast rows={rows} f={f} />
            <EchAging rows={rows} lateRows={rowsNoAge} f={f} setF={setF} />
          </div>
          {/* iter449 5.4 — 2 colonnes : ÉCHÉANCIER | ANALYSE */}
          <div className="grid min-w-0" style={{ gridTemplateColumns: "minmax(0, 1.65fr) minmax(0, 1fr)", gap: 10, alignItems: "start" }} data-testid="ech-two-cols">
            <EchTable rows={rows} />
            <EchAnalysis rows={rows} clientName={!f.all_clients && f.clients.length === 1 ? data.items.find((r) => r.contact_id === f.clients[0])?.client : null} />
          </div>
        </div>
      </div>
    </TresoShell>
  );
}
